import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { NavigationDirectionProvider, useNavigationDirection } from "./contexts/NavigationDirection";

const HomePage = lazy(() => import("./pages/HomePage").then((m) => ({ default: m.HomePage })));
const ProfilePage = lazy(() => import("./pages/ProfilePage").then((m) => ({ default: m.ProfilePage })));
const PublishPage = lazy(() => import("./pages/PublishPage").then((m) => ({ default: m.PublishPage })));
const TopicsPage = lazy(() => import("./pages/TopicsPage").then((m) => ({ default: m.TopicsPage })));
const TreeholePage = lazy(() => import("./pages/TreeholePage").then((m) => ({ default: m.TreeholePage })));
const PostDetailPage = lazy(() => import("./pages/PostDetailPage").then((m) => ({ default: m.PostDetailPage })));
const TopicDetailPage = lazy(() => import("./pages/TopicDetailPage").then((m) => ({ default: m.TopicDetailPage })));
const UserDetailPage = lazy(() => import("./pages/UserDetailPage").then((m) => ({ default: m.UserDetailPage })));
const ChatPage = lazy(() => import("./pages/ChatPage").then((m) => ({ default: m.ChatPage })));
const LoginPage = lazy(() => import("./pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const SearchPage = lazy(() => import("./pages/SearchPage").then((m) => ({ default: m.SearchPage })));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage").then((m) => ({ default: m.NotificationsPage })));
const BoardDetailPage = lazy(() => import("./pages/BoardDetailPage").then((m) => ({ default: m.BoardDetailPage })));

function PageLoading() {
  return (
    <div className="page" style={{ display: "flex", flexDirection: "column", gap: "16px", paddingTop: "20px" }}>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="skeleton-shimmer" style={{ height: "16px", width: `${90 - i * 8}%`, borderRadius: "var(--radius-md)" }} />
      ))}
    </div>
  );
}

/** Inner component that uses navigation direction to animate page transitions */
function AnimatedRoutes() {
  const { direction, pageKey } = useNavigationDirection();
  const location = useLocation();

  const transitionClass =
    direction === "push" ? "page-push" :
    direction === "pop" ? "page-pop" :
    direction === "tab" ? "page-tab-switch" : "";

  return (
    <div className={`page-transition-wrap ${transitionClass}`} key={pageKey}>
      <Suspense fallback={<PageLoading />}>
        <Routes location={location}>
          <Route element={<HomePage />} path="/" />
          <Route element={<TopicsPage />} path="/topics" />
          <Route element={<PublishPage />} path="/publish" />
          <Route element={<TreeholePage />} path="/treehole" />
          <Route element={<ProfilePage />} path="/profile" />
          <Route element={<PostDetailPage />} path="/post/:id" />
          <Route element={<TopicDetailPage />} path="/topic/:id" />
          <Route element={<UserDetailPage />} path="/user/:id" />
          <Route element={<ChatPage />} path="/chat/:id" />
          <Route element={<LoginPage />} path="/login" />
          <Route element={<SearchPage />} path="/search" />
          <Route element={<NotificationsPage />} path="/notifications" />
          <Route element={<BoardDetailPage />} path="/board/:id" />
          <Route element={<Navigate replace to="/" />} path="*" />
        </Routes>
      </Suspense>
    </div>
  );
}

export default function App() {
  return (
    <AppShell>
      <NavigationDirectionProvider>
        <AnimatedRoutes />
      </NavigationDirectionProvider>
    </AppShell>
  );
}
